import { getLocalDebugHost } from './debug';
import message from 'antd/es/message';

import env from './env';
import { headUserInfoStr } from './address';
import { recordApi } from './requestRecord';
import { integrateQueryToUrl, parseUrlQuery } from './url';
import { getSignature } from './sign';
import {
  SWITCH_USE_NONCE_DEFAULT,
  SWITCH_SIGN_FIELD_POSITION,
  SIGN_HDS,
  getRequestSource,
} from './security';
import { sleep } from './utils';
import { getCurrentLng } from './i18n';
import { logout } from './address';

export const BASE_URL = env.isProduction
  ? 'https://api.debank.com'
  : getLocalDebugHost() || process?.env?.REACT_APP_BASE_URL || 'https://api.debank.com';

type RequestOptions = {
  timeout?: number;
  base_url?: string;
  data?: RequestInit['body'] | Record<string, string | number | boolean | undefined> | null;
  noSession?: boolean;
  noSource?: boolean;
  signal?: AbortController['signal'];
  nonce?: boolean;
  retry?: number;
  hideMessage?: boolean;
  _currentRetry?: number;
  credential?: string;
  noLng?: boolean;
} & Pick<RequestInit, 'headers' | 'method' | 'body' | 'credentials'>;

const DEFAULT_TIMEOUT = 10 * 1000;

const _request = async (url: string, options: RequestOptions = {}): Promise<any> => {
  const finishApi = recordApi();

  const {
    timeout = DEFAULT_TIMEOUT,
    headers,
    base_url,
    data: _data,
    method,
    noSession,
    noSource,
    signal,
    nonce: _useNonce = SWITCH_USE_NONCE_DEFAULT,
    retry,
    _currentRetry = 0,
    credential,
    noLng,
    ...rest
  } = options;
  let useNonce = _useNonce;

  const _headers: HeadersInit = {
    ...headers,
  };

  const lng = getCurrentLng() as string;
  if (lng && !noLng) {
    (_headers as any)['X-Trans-Language'] = lng.toUpperCase();
  }

  if (!noSource) {
    (_headers as any).source = getRequestSource();
  }

  const user_info = credential ?? headUserInfoStr;
  if (user_info && !noSession) {
    Object.assign(_headers, {
      account: user_info,
    });
  }

  const requestAbort = new AbortController();

  // hook pass in abort to request abortController
  let abortRequest: null | (() => void) = null;
  if (signal) {
    if (signal.aborted) {
      requestAbort.abort();
      return Promise.reject(new Error('AbortError'));
    } else {
      abortRequest = () => {
        // console.log('****cancel request****', url, params?.user_addr);
        requestAbort.abort();
        if (abortRequest) {
          signal.removeEventListener('abort', abortRequest);
          abortRequest = null;
        }
      };
      signal.addEventListener('abort', abortRequest);
    }
  }

  const fetchOptions: RequestInit = {
    ...rest,
    headers: _headers,
    method,
    mode: 'cors',
    signal: requestAbort.signal,
  };

  let resource = `${(base_url || BASE_URL).replace(/\/$/, '')}${url}`;
  const data = omitNil(_data);
  const isDataValid = data && Object.keys(data).length;

  let qsDict: Record<string, string> = {};

  if (method === 'GET') {
    qsDict = { ...(data as Record<string, string>) };
  } else if (
    isDataValid &&
    (_headers as Record<string, string>)['Content-Type'] === 'application/json'
  ) {
    fetchOptions.body = JSON.stringify(data);
  } else if (
    isDataValid &&
    (_headers as Record<string, string>)['Content-Type'] === 'application/x-www-form-urlencoded'
  ) {
    const qs = new URLSearchParams(data as Record<string, string>);
    fetchOptions.body = qs.toString();
  } else {
    fetchOptions.body = data as BodyInit;
  }
  let abortTimer: number;

  if (typeof timeout === 'number' && timeout !== Infinity) {
    if (timeout <= 0) {
      requestAbort.abort();
      return Promise.reject(new Error('Timeout'));
    }
    abortTimer = window.setTimeout(() => {
      requestAbort.abort('Timeout');
    }, timeout);
  }

  if (useNonce) {
    const urlInfo = parseUrlQuery(resource);
    const { nonce, ts, signature, version } = await getSignature(
      { ...urlInfo.query, ...qsDict },
      (fetchOptions.method as any) || 'GET',
      urlInfo.canoicalPath,
    );
    const signObj = {
      sign: signature + '',
      ts: ts + '',
      n: nonce + '',
      v: version,
    };
    if (SWITCH_SIGN_FIELD_POSITION === 'query') {
      Object.assign(qsDict, signObj);
    } else if (SWITCH_SIGN_FIELD_POSITION === 'headers') {
      (_headers as any)[SIGN_HDS[0]] = encodeURIComponent(signObj.ts);
      (_headers as any)[SIGN_HDS[1]] = encodeURIComponent(signObj.n);
      (_headers as any)[SIGN_HDS[2]] = encodeURIComponent(signObj.v);
      (_headers as any)[SIGN_HDS[3]] = encodeURIComponent(signObj.sign);
    }
  }

  resource = integrateQueryToUrl(resource, qsDict);

  return fetch(resource, fetchOptions)
    .then(res => {
      if (abortTimer) {
        clearTimeout(abortTimer);
      }
      return res;
    })
    .then(res => {
      if (res.ok) {
        return res.json();
      }
      return Promise.reject(res);
    })
    .catch((err: any) => {
      if (abortTimer) {
        clearTimeout(abortTimer);
      }
      if (retry && canRetryError(err) && _currentRetry < retry) {
        options._currentRetry = _currentRetry + 1;
        return _request(url, options);
      }

      return err && typeof err === 'object' && 'json' in err
        ? err.json().then(
            // 即使是 500 / 429，也可能有 { error_code, error_msg }
            (res: any) => Promise.reject(res),
            (_: any) => Promise.reject(err),
          )
        : Promise.reject(err);
    })
    .finally(() => {
      finishApi();
    })
    .then((res: { error_code: number; data?: any; error_msg: any }) => {
      if (!res.error_code) {
        return res.data !== undefined ? res.data : res;
      }

      if (res.error_code === 401) {
        // 如果有地址, 就不用提示连接钱包了 并且还有删除老账户的验证状态
        try {
          if (user_info) {
            if (!options.hideMessage) {
              message.info(res.error_msg || '401');
            }
            logout(JSON.parse(user_info)?.user_addr);
          }
        } catch (error) {}
      }

      return Promise.reject(res || 'error');
    });
};

export const canRetryError = (err: any) => {
  return (
    // http code
    err?.status
      ? err.status >= 500
      : // typeError => cors error, network unstable
        err?.name === 'TypeError'
  );
};

const get = <T>(
  url: string,
  data?: Record<string, string | number | boolean | undefined> | null,
  options?: RequestOptions,
): Promise<T> =>
  _request(url, {
    data,
    method: 'GET',
    ...options,
  });

const ASYNC_JOB_TIMEOUT = 10 * 60 * 1000;
const ASYNC_JOB_RETRY_DELAY = 1.5 * 1000;

// https://debankglobal.larksuite.com/wiki/wikusccSBNCTcmh3KIXCCCoR44b
const async = <T>(
  url: string,
  data?: Record<string, string | number | boolean | undefined> | null,
  options?: RequestOptions & {
    retryDelay?: number;
  },
): Promise<T> => {
  const _option = {
    timeout: ASYNC_JOB_TIMEOUT,
    retryDelay: ASYNC_JOB_RETRY_DELAY,
    ...options,
  };
  const startTime = +new Date();

  return (
    _request(url, {
      data,
      method: 'GET',
      ..._option,
    })
      // 内部报错会抛在外面的 error_code
      // 如果有 result，表示 job 执行是成功的
      .then(
        (res: {
          create_at: number;
          id: string;
          result: {
            create_at: number;
            id: string;
            data: any;
          };
          job: { create_at: number; id: string; status: 'pending' | 'running' };
        }) => {
          // 有未过期的结果
          if (res.result) {
            return res.result.data;
          }

          const deltaTime = +new Date() - startTime;
          _option.timeout = _option.timeout - deltaTime - _option.retryDelay;

          if (_option.timeout < 0) {
            _option.timeout = 0;
          }

          // 继续请求，默认 5s 间隔
          return sleep(_option.retryDelay, _option.signal).then(() => async(url, data, _option));
        },
      )
  );
};

const post = <T>(url: string, data?: any, options?: RequestOptions): Promise<T> =>
  _request(url, {
    data,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    ...options,
  });

const _delete = <T>(url: string, data?: any, options?: RequestOptions): Promise<T> =>
  _request(url, {
    data,
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    ...options,
  });

const _put = <T>(url: string, data?: any, options?: RequestOptions): Promise<T> =>
  _request(url, {
    data,
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    ...options,
  });

function type(obj: any) {
  return Object.prototype.toString
    .call(obj)
    .replace(/^\[object (.+)\]$/, '$1')
    .toLowerCase();
}

// 简单的，只过滤一层 Object
export const omitNil = (obj?: any) => {
  if (!obj || type(obj) !== 'object') {
    return obj;
  }

  return Object.entries(obj).reduce((m: Record<string, any>, [k, v]) => {
    if (v != null) {
      m[k] = v;
    }

    return m;
  }, {});
};

export const request = { get, post, delete: _delete, put: _put, async };

const OAUTH_BASE_URL = 'https://api.connect.debank.com';

export const oauthRequest = {
  get: <T>(
    url: string,
    data?: Record<string, string | number | boolean> | null,
    options?: RequestOptions,
  ): Promise<T> => get(url, data, { ...options, base_url: OAUTH_BASE_URL, noLng: true }),

  post: <T>(url: string, data?: any, options?: RequestOptions): Promise<T> =>
    post(url, data, {
      ...options,
      base_url: OAUTH_BASE_URL,
      noLng: true,
    }),
};

export default request;
